import type { ComponentProps } from 'react';
import type { Icon } from '@launchpad-ui/icons';

export type LaunchpadIconName = ComponentProps<typeof Icon>['name'];

export const ARTIFACT_TYPE_LABELS: Record<string, string> = {
  // Backend stores PRs as `pr` (AgentArtifactTypePR); some FE paths still use
  // `pull_request`. Keep both so chips and detail headings stay labeled.
  pr: 'Pull request',
  pull_request: 'Pull request',
  flag: 'Flag',
  dashboard: 'Dashboard',
  graph: 'Graph',
  ai_config: 'AI config',
  agent_graph: 'Agent graph',
  log_alert: 'Alert',
  error_group: 'Error group',
  metric_monitor: 'Metric monitor',
  trace: 'Trace',
  session: 'Session',
  jira_issue: 'Jira issue',
};

export const ARTIFACT_ICONS: Record<string, LaunchpadIconName> = {
  pull_request: 'arrow-merge',
  flag: 'flag-outline',
  dashboard: 'chart-dashboard',
  graph: 'chart-line',
  ai_config: 'chip-ai',
  agent_graph: 'sparkle',
  log_alert: 'notifications-outline',
  error_group: 'warning-outline',
  metric_monitor: 'pulse',
  trace: 'timeline-text',
  session: 'play-circle-outline',
  jira_issue: 'link',
};

// Backend / agent writers sometimes emit short aliases (`pr`, `alert`) instead of the
// canonical type keys above. Map those so chips, icons, and status formatting stay
// consistent regardless of which form a row was stored with.
const ARTIFACT_TYPE_ALIASES: Record<string, string> = {
  pr: 'pull_request',
  pullrequest: 'pull_request',
  alert: 'log_alert',
};

// Normalize for display/lookup only. Do not rewrite the type used in routes or
// GraphQL variables — those must match the stored row's `type` exactly.
export const canonicalizeArtifactType = (type: string): string => {
  const normalized = type.trim().toLowerCase();
  return ARTIFACT_TYPE_ALIASES[normalized] ?? normalized;
};

const toSentenceCaseLabel = (raw: string): string => {
  const trimmed = raw.trim().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
  if (trimmed === '') {
    return '';
  }
  const lower = trimmed.toLowerCase();
  return `${lower.charAt(0).toUpperCase()}${lower.slice(1)}`;
};

export const getArtifactTypeLabel = (type: string): string => {
  const canonical = canonicalizeArtifactType(type);
  return ARTIFACT_TYPE_LABELS[canonical] ?? toSentenceCaseLabel(canonical);
};

export const getArtifactTypeIcon = (type: string): LaunchpadIconName =>
  ARTIFACT_ICONS[canonicalizeArtifactType(type)] ?? 'link';

// Known lifecycle statuses for pull requests (and any other artifact that reuses the
// same vocabulary). Kept lowercase for lookup; UI labels are sentence case.
export type ArtifactStatusTone = 'open' | 'merged' | 'closed' | 'draft' | 'neutral';

const ARTIFACT_STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  merged: 'Merged',
  closed: 'Closed',
  draft: 'Draft',
};

const ARTIFACT_STATUS_TONES: Record<string, ArtifactStatusTone> = {
  open: 'open',
  merged: 'merged',
  closed: 'closed',
  draft: 'draft',
};

export const normalizeArtifactStatus = (status: string): string => status.trim().toLowerCase();

// Human-readable label for chip/detail badges. Known PR lifecycle statuses get
// sentence-case labels; anything else is sentence-cased as a fallback.
export const getArtifactStatusLabel = (status: string): string => {
  const normalized = normalizeArtifactStatus(status);
  if (normalized === '') {
    return '';
  }
  return ARTIFACT_STATUS_LABELS[normalized] ?? toSentenceCaseLabel(normalized);
};

// Maps a status string to a visual tone used by ArtifactChip's status badge. Unknown
// values fall back to neutral so we never invent color for an unrecognized state.
export const getArtifactStatusTone = (status: string): ArtifactStatusTone => {
  const normalized = normalizeArtifactStatus(status);
  return ARTIFACT_STATUS_TONES[normalized] ?? 'neutral';
};

// Only pull requests keep a trustworthy, updated status today (open/draft/merged/closed
// via create + GitHub webhooks). Flag/alert rows are stamped `active` at create time and
// are not refreshed when the underlying entity is disabled or archived — so showing that
// badge is misleading. Gate the chip/detail badge on PR types until those writers exist.
export const shouldDisplayArtifactStatus = (type: string, status: string | null | undefined): boolean => {
  if (!status?.trim()) {
    return false;
  }
  return canonicalizeArtifactType(type) === 'pull_request';
};

// Prefer the stored title; when missing, turn PR URLs into a short `owner/repo#123`
// label so chips don't dump a full GitHub URL into the list.
export const getArtifactDisplayTitle = (type: string, title: string | null | undefined, externalId: string): string => {
  const trimmedTitle = title?.trim();
  if (trimmedTitle) {
    return trimmedTitle;
  }
  if (canonicalizeArtifactType(type) === 'pull_request') {
    const short = formatPullRequestShortLabel(externalId);
    if (short) {
      return short;
    }
  }
  return externalId.trim();
};

function formatPullRequestShortLabel(raw: string): string | null {
  const canonical = canonicalizePullRequestExternalId(raw.trim());
  try {
    const parsed = new URL(canonical.includes('://') ? canonical : `https://${canonical}`);
    const match = parsed.pathname.match(/^\/([^/]+)\/([^/]+)\/pull\/(\d+)\/?$/);
    if (!match) {
      return null;
    }
    const [, owner, repo, number] = match;
    return `${owner}/${repo}#${number}`;
  } catch {
    return null;
  }
}

// Artifact URLs originate from backend records and may contain user-influenced content
// (PR titles, agent output, etc.). Restrict to http(s) so a malicious `javascript:` or
// `data:` URI can't run in this origin via middle-click, Ctrl-click, or keyboard activation.
// Reused by ArtifactChip and the artifact detail page — every place we render the raw URL
// in an anchor or hand it to a window.open / startConversationWithSource caller.
export const isSafeExternalUrl = (raw: string | null | undefined): boolean => {
  if (!raw) {
    return false;
  }
  try {
    const parsed = new URL(raw);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
};

// Artifact types whose pages live inside this app (same origin as wherever Vega is
// being viewed). Keep in sync with the keys in ARTIFACT_TYPE_LABELS above — this is
// every label-mapped type except the external `pull_request`. The other external types
// (jira_issue, confluence_page, slack_message) are genuinely third-party and stay absent
// so their absolute URLs are preserved rather than re-based onto this origin.
const INTERNAL_ARTIFACT_TYPES = new Set([
  'flag',
  'dashboard',
  'graph',
  'ai_config',
  'agent_graph',
  'log_alert',
  'error_group',
  'metric_monitor',
  'trace',
  'session',
]);

export const isInternalArtifactType = (type: string): boolean =>
  INTERNAL_ARTIFACT_TYPES.has(canonicalizeArtifactType(type));

// Resolve an artifact's URL for linking. Internal artifacts are stored with an
// absolute URL whose host is baked in at creation time from the backend's
// configured frontend base (e.g. http://localhost:3002 in dev), so opening it
// verbatim sends you to that host instead of wherever you're actually viewing
// Vega. Re-base internal URLs onto the current origin (keeping path + query +
// hash) so the link follows the current domain. External artifacts keep their
// absolute URL. Returns null for anything that isn't a safe http(s) URL.
export const resolveArtifactUrl = (type: string, raw: string | null | undefined): string | null => {
  if (!raw || !isSafeExternalUrl(raw)) {
    return null;
  }
  if (!isInternalArtifactType(type) || typeof window === 'undefined') {
    return raw;
  }
  try {
    const parsed = new URL(raw);
    return `${window.location.origin}${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return raw;
  }
};

// Mirrors backend store.canonicalizeExternalID so inline-token lookups against the
// conversation's known artifacts resolve to the same row the backend would, even if
// the emitted externalId is slightly non-canonical (trailing slash, mixed-case host, etc.).
export function canonicalizeArtifactExternalId(type: string, raw: string): string {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return trimmed;
  }
  if (canonicalizeArtifactType(type) === 'pull_request') {
    return canonicalizePullRequestExternalId(trimmed);
  }
  return trimmed;
}

function canonicalizePullRequestExternalId(raw: string): string {
  let candidate = raw;
  if (!candidate.includes('://')) {
    candidate = `https://${candidate}`;
  }
  try {
    const parsed = new URL(candidate);
    parsed.protocol = 'https:';
    parsed.hostname = parsed.hostname.toLowerCase();
    parsed.pathname = parsed.pathname.replace(/\/+$/, '');
    parsed.hash = '';
    parsed.search = '';
    return parsed.toString();
  } catch {
    return raw;
  }
}
